import { DashboardDataItem } from "./types";
import { formatShortDate } from "../PopulationPulse/utils/formatDates";
import {
  scanCompletenessHasActivity,
  scanTypeHasActivity,
  toScanCompletenessValue,
  toScanTypeCoverageValue,
} from "./scanCompleteness";

export const NO_SCAN_DATA_LAST_28_DAYS = "No signals in the last 28 days.";
export const SIGNAL_COVERAGE_HEADING = "All-time reach";
export const SIGNAL_COVERAGE_NOTE =
  "Share of enrolled people who have sent a signal. A person with more than one still counts as one.";
export const SIGNAL_ACTIVITY_HEADING = "Current Population Activity";
export const SIGNAL_ACTIVITY_NOTE =
  "Signal activity over the last 28 days: who is active, who is new, who is returning, and how that compares with the previous 28 days.";
export const SIGNAL_ACTIVITY_PLACEHOLDER =
  "More advanced data appears after a minimum of 28 days of history.";
export const FACE_VOICE_MONTHLY_LABEL = "FACE + VOICE SIGNALS (MONTHLY)";
export const WEARABLE_DAYS_MONTHLY_LABEL = "Wearable days (monthly)";

/** Section headings already say coverage vs last-28-day activity. */
export function reportingPieTitle(label: string): string {
  const withoutWindow = label.replace(/ \((?:overall|last 28 days)\)$/i, "");
  if (withoutWindow === "Face scans") {
    return "Face";
  }

  if (withoutWindow === "Voice scans") {
    return "Voice";
  }

  if (
    withoutWindow === "Scan completeness" ||
    withoutWindow === "Population Coverage"
  ) {
    return "Population Coverage";
  }

  return withoutWindow;
}

function peopleParticipating(count: number): string {
  return count === 1
    ? "1 person participating"
    : `${count} people participating`;
}

/** Reach (distinct people) and depth (returns) for a Face, Voice, or Wearables tile. */
export function signalParticipationLines(
  label: string,
  uniqueUsers: number,
  totalEvents: number,
): { reach: string; depth: string } {
  return {
    reach: peopleParticipating(uniqueUsers),
    depth: signalDepthLine(label, totalEvents),
  };
}

export function allSignalsLines(counts: {
  threeOfThree: number;
  twoOfThree: number;
  oneOfThree: number;
  scanCount: number;
  wearableDays: number;
}): { reach: string; scans: string; wearableDays: string } {
  const people = counts.threeOfThree + counts.twoOfThree + counts.oneOfThree;
  const signalNoun = counts.scanCount === 1 ? "signal" : "signals";
  const dayNoun = counts.wearableDays === 1 ? "wearable day" : "wearable days";
  return {
    reach: peopleParticipating(people),
    scans: `${counts.scanCount} face and voice ${signalNoun}`,
    wearableDays: `${counts.wearableDays} ${dayNoun}`,
  };
}

function signalDepthLine(label: string, count: number): string {
  if (label.startsWith("Wearables")) {
    return count === 1 ? "1 day" : `${count} days`;
  }

  return count === 1 ? "1 signal" : `${count} signals`;
}

function readActivityNumber(
  record: Record<string, unknown>,
  camel: string,
  pascal: string,
): number | null {
  const raw = record[camel] ?? record[pascal];
  if (raw == null || raw === "") {
    return null;
  }

  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function peopleWord(count: number): string {
  return count === 1 ? "1 person" : `${count} people`;
}

/** Last-28-day activity lines for the Current Population Activity section. */
export function populationActivityLines(value: unknown): string[] {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return [];
  }

  const record = value as Record<string, unknown>;
  const derivedActive =
    (readActivityNumber(record, "threeOfThree", "ThreeOfThree") ?? 0) +
    (readActivityNumber(record, "twoOfThree", "TwoOfThree") ?? 0) +
    (readActivityNumber(record, "oneOfThree", "OneOfThree") ?? 0);
  const active =
    readActivityNumber(record, "activePeople", "ActivePeople") ?? derivedActive;
  const signals =
    readActivityNumber(record, "faceVoiceSignals", "FaceVoiceSignals") ??
    readActivityNumber(record, "scanCount", "ScanCount") ??
    0;
  const days = readActivityNumber(record, "wearableDays", "WearableDays") ?? 0;
  const signalNoun = signals === 1 ? "signal" : "signals";
  const dayNoun = days === 1 ? "day" : "days";
  const lines = [];
  const returning = readActivityNumber(
    record,
    "returningPeople",
    "ReturningPeople",
  );
  const newcomers = readActivityNumber(record, "newPeople", "NewPeople");
  if (returning != null) {
    lines.push(`Returning: ${peopleWord(returning)}`);
  }

  if (newcomers != null) {
    lines.push(`New participants: ${peopleWord(newcomers)}`);
  }

  const previous = readActivityNumber(
    record,
    "previousActivePeople",
    "PreviousActivePeople",
  );
  if (previous != null) {
    lines.push(activityChangeLine(active, previous));
  }

  return lines;
}

function activityChangeLine(active: number, previous: number): string {
  const delta = active - previous;
  if (delta === 0) {
    return "No change in active members versus previous 28 days";
  }

  if (delta === 1) {
    return "1 more active member than previous 28 days";
  }

  if (delta === -1) {
    return "1 fewer active member than previous 28 days";
  }

  if (delta > 0) {
    return `${delta} more active members than previous 28 days`;
  }

  return `${Math.abs(delta)} fewer active members than previous 28 days`;
}

export function reportingSectionNote(heading: string): string | null {
  if (heading === SIGNAL_COVERAGE_HEADING) {
    return SIGNAL_COVERAGE_NOTE;
  }

  if (heading === SIGNAL_ACTIVITY_HEADING) {
    return SIGNAL_ACTIVITY_NOTE;
  }

  return null;
}

export function lastDataUpdateLine(items: DashboardDataItem[]): string | null {
  const iso = lastDataUpdateUtc(items);
  if (!iso) {
    return null;
  }

  const label = formatShortDate(iso);
  if (!label || label === iso) {
    return `Last data update: ${iso}`;
  }

  return `Last data update: ${label}`;
}

function lastDataUpdateUtc(items: DashboardDataItem[]): string | null {
  for (const item of items) {
    if (
      item.label !== "Population Coverage (overall)" &&
      item.label !== "Population Coverage (last 28 days)"
    ) {
      continue;
    }

    if (item.value == null || typeof item.value !== "object" || Array.isArray(item.value)) {
      continue;
    }

    const record = item.value as Record<string, unknown>;
    const raw = record.lastDataUpdateUtc ?? record.LastDataUpdateUtc;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
  }

  return null;
}

export type ReportingTile =
  | { kind: "item"; item: DashboardDataItem }
  | { kind: "last28Empty" }
  | { kind: "activityPlaceholder" }
  | { kind: "heading"; text: string };

function isScanCompletenessLabel(label: string): boolean {
  return (
    label.startsWith("Scan completeness") ||
    label.startsWith("Population Coverage")
  );
}

function isScanTypeLabel(label: string): boolean {
  return (
    label.startsWith("Face scans") ||
    label.startsWith("Voice scans") ||
    label.startsWith("Wearables")
  );
}

function isLast28ScanLabel(label: string): boolean {
  return (
    label.includes("(last 28 days)") &&
    (isScanCompletenessLabel(label) || isScanTypeLabel(label))
  );
}

function isMonthlyTrendLabel(label: string): boolean {
  return (
    label === FACE_VOICE_MONTHLY_LABEL || label === WEARABLE_DAYS_MONTHLY_LABEL
  );
}

function isOverallScanChart(label: string): boolean {
  return (
    isMonthlyTrendLabel(label) ||
    ((isScanCompletenessLabel(label) || isScanTypeLabel(label)) &&
      !label.includes("(last 28 days)"))
  );
}

function monthlyHasActivity(value: unknown): boolean {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.some((point) => {
    if (point == null || typeof point !== "object") {
      return false;
    }

    const raw =
      (point as { value?: unknown; Value?: unknown }).value ??
      (point as { Value?: unknown }).Value;
    return Number(raw) > 0;
  });
}

export function scanItemHasActivity(item: DashboardDataItem): boolean {
  if (isMonthlyTrendLabel(item.label)) {
    return monthlyHasActivity(item.value);
  }

  if (isScanCompletenessLabel(item.label)) {
    return scanCompletenessHasActivity(toScanCompletenessValue(item.value));
  }

  if (isScanTypeLabel(item.label)) {
    return scanTypeHasActivity(toScanTypeCoverageValue(item.value));
  }

  return true;
}

function isLast28Label(label: string): boolean {
  return label.includes("(last 28 days)");
}

export function reportingColSpan(
  label: string,
  visibleLabels: string[],
): string {
  const monthlyChartCount = visibleLabels.filter((visible) =>
    isMonthlyTrendLabel(visible),
  ).length;
  const overallModalities = visibleLabels.filter(
    (visible) => isScanTypeLabel(visible) && !isLast28Label(visible),
  ).length;

  if (label === "Organisation Health Score") {
    if (monthlyChartCount === 1) {
      return "lg:col-span-6";
    }
    if (monthlyChartCount >= 2) {
      return "lg:col-span-4";
    }
    return "lg:col-span-12";
  }

  if (isMonthlyTrendLabel(label)) {
    return monthlyChartCount >= 2 ? "lg:col-span-4" : "lg:col-span-6";
  }

  if (
    isScanTypeLabel(label) &&
    !isLast28Label(label) &&
    monthlyChartCount === 0
  ) {
    const completenessVisible = visibleLabels.some(
      (visible) => isScanCompletenessLabel(visible) && !isLast28Label(visible),
    );
    if (!completenessVisible) {
      if (overallModalities >= 3) {
        return "lg:col-span-4";
      }
      if (overallModalities === 2) {
        return "lg:col-span-6";
      }
      if (overallModalities === 1) {
        return "lg:col-span-12";
      }
    }
  }

  if (isScanCompletenessLabel(label) || isScanTypeLabel(label)) {
    return "lg:col-span-3";
  }

  return "lg:col-span-12";
}

function isChartableEmptyScanType(item: DashboardDataItem): boolean {
  return (
    isScanTypeLabel(item.label) && toScanTypeCoverageValue(item.value) != null
  );
}

export function reportingTiles(items: DashboardDataItem[]): ReportingTile[] {
  const last28 = items.filter((item) => isLast28ScanLabel(item.label));
  const hasOlderScanActivity = items.some(
    (item) => isOverallScanChart(item.label) && scanItemHasActivity(item),
  );
  const last28HasActivity = last28.some((item) => scanItemHasActivity(item));
  const showLast28Notice =
    hasOlderScanActivity && last28.length > 0 && !last28HasActivity;
  const activityLocked = isActivityLocked(items);
  let noticePlaced = false;
  let placeholderPlaced = false;
  const tiles: ReportingTile[] = [];

  for (const item of items) {
    if (activityLocked && isLast28ScanLabel(item.label)) {
      if (!placeholderPlaced) {
        tiles.push({ kind: "activityPlaceholder" });
        placeholderPlaced = true;
      }
      continue;
    }

    if (isLast28ScanLabel(item.label)) {
      if (
        scanItemHasActivity(item) ||
        (last28HasActivity && isChartableEmptyScanType(item))
      ) {
        tiles.push({ kind: "item", item });
      } else if (showLast28Notice && !noticePlaced) {
        tiles.push({ kind: "last28Empty" });
        noticePlaced = true;
      }
      continue;
    }

    if (
      !isMonthlyTrendLabel(item.label) &&
      isOverallScanChart(item.label) &&
      !scanItemHasActivity(item)
    ) {
      if (hasOlderScanActivity && isChartableEmptyScanType(item)) {
        tiles.push({ kind: "item", item });
      }
      continue;
    }

    tiles.push({ kind: "item", item });
  }

  return withSectionHeadings(tiles);
}

function isOverallScanSection(label: string): boolean {
  return (
    (isScanCompletenessLabel(label) || isScanTypeLabel(label)) &&
    !isLast28Label(label)
  );
}

function isActivityLocked(items: DashboardDataItem[]): boolean {
  const days = signalHistoryDays(items);
  return days != null && days < 28;
}

function signalHistoryDays(items: DashboardDataItem[]): number | null {
  const coverage = items.find(
    (item) => item.label === "Population Coverage (last 28 days)",
  );
  if (
    coverage?.value == null ||
    typeof coverage.value !== "object" ||
    Array.isArray(coverage.value)
  ) {
    return null;
  }

  const record = coverage.value as Record<string, unknown>;
  const raw = record.signalHistoryDays ?? record.SignalHistoryDays;
  if (raw == null || raw === "") {
    return null;
  }

  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function withSectionHeadings(tiles: ReportingTile[]): ReportingTile[] {
  const result: ReportingTile[] = [];
  let overallPlaced = false;
  let last28Placed = false;

  for (const tile of tiles) {
    const label = tile.kind === "item" ? tile.item.label : "";
    const isLast28 =
      tile.kind === "last28Empty" ||
      tile.kind === "activityPlaceholder" ||
      (tile.kind === "item" && isLast28ScanLabel(label));
    if (isLast28) {
      if (!last28Placed && tile.kind !== "activityPlaceholder") {
        result.push({ kind: "heading", text: SIGNAL_ACTIVITY_HEADING });
      }
      last28Placed = true;
      result.push(tile);
      continue;
    }

    if (tile.kind === "item" && isOverallScanSection(label)) {
      if (!overallPlaced) {
        result.push({ kind: "heading", text: SIGNAL_COVERAGE_HEADING });
        overallPlaced = true;
      }
      result.push(tile);
      continue;
    }

    result.push(tile);
  }

  return result;
}
